import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { getSessionId } from '../../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS = ['Roller shades', 'Solar shades', 'Roman shades', 'Cellular shades', 'Zebra shades', 'Blinds', 'Drapery', 'Motorization / smart home', 'Not sure yet'];

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label>{children}</div>
);

// Empty-cart path on /quote: a pricing request (not an instant priced quote), saved as a Quote Page request with source attribution.
export const GeneralQuoteRequest = ({ user, token, source }) => {
  const navigate = useNavigate();
  const [f, setF] = useState({ customer_name: user?.name || '', customer_email: user?.email || '', customer_phone: '', billing_zip: '', project_type: 'residential', rooms_count: '', request_details: '' });
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { if (done) window.scrollTo({ top: 0, behavior: 'smooth' }); }, [done]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (p) => setProducts(products.includes(p) ? products.filter((x) => x !== p) : [...products, p]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API_URL}/quotes/create`, { ...f, ...source, general_request: true, products_interest: products.join(', '), shipping_zip: f.billing_zip },
        { params: user ? {} : { session_id: getSessionId() }, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send your request. Please try again or call us.');
    } finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-16" data-testid="general-quote-success">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8 text-amber-600" /></div>
        <h1 className="text-3xl font-serif mb-3">Request received</h1>
        <p className="text-neutral-600">Thank you, {f.customer_name.split(' ')[0]}. Our team will review what you need and get back to you with pricing, usually within one business day.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <Button variant="outline" onClick={() => navigate('/products')}>Browse products</Button>
          <Button className="btn-primary" onClick={() => navigate('/consultation')}><Calendar className="w-4 h-4 mr-2" />Book a free consultation</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-2xl mx-auto" data-testid="general-quote-request">
      <h1 className="text-3xl md:text-4xl font-serif mb-3">Request a Quote</h1>
      <p className="text-neutral-600 mb-2">Tell us what you need and our team will prepare pricing for you. This is a request for pricing, not an instant quote.</p>
      <p className="text-sm text-neutral-500 mb-8">Prefer an instant estimate? <button type="button" onClick={() => navigate('/products')} className="underline text-amber-700 hover:text-amber-800" data-testid="general-quote-configure-link">Configure a product</button> first, and it will appear here.</p>
      <form onSubmit={submit} className="space-y-5 bg-white border border-neutral-200 p-6 md:p-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="gq-name" label="Full name *"><Input id="gq-name" required value={f.customer_name} onChange={set('customer_name')} data-testid="general-quote-name" /></Field>
          <Field id="gq-email" label="Email *"><Input id="gq-email" type="email" required value={f.customer_email} onChange={set('customer_email')} data-testid="general-quote-email" /></Field>
          <Field id="gq-phone" label="Phone"><Input id="gq-phone" type="tel" value={f.customer_phone} onChange={set('customer_phone')} data-testid="general-quote-phone" /></Field>
          <Field id="gq-zip" label="Project ZIP code"><Input id="gq-zip" value={f.billing_zip} onChange={set('billing_zip')} data-testid="general-quote-zip" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="gq-type" label="Project type">
            <select id="gq-type" value={f.project_type} onChange={set('project_type')} className="w-full h-10 border border-neutral-200 px-3 text-sm bg-white" data-testid="general-quote-type">
              <option value="residential">Residential</option><option value="commercial">Commercial</option><option value="architectural">Architectural / trade</option>
            </select>
          </Field>
          <Field id="gq-rooms" label="Approx. number of windows"><Input id="gq-rooms" value={f.rooms_count} onChange={set('rooms_count')} placeholder="e.g. 6 windows in 3 rooms" data-testid="general-quote-rooms" /></Field>
        </div>
        <div className="space-y-1.5">
          <Label>Products you're interested in</Label>
          <div className="flex flex-wrap gap-2">
            {PRODUCTS.map((p) => (
              <button key={p} type="button" onClick={() => toggle(p)} data-testid={`general-quote-product-${p.split(' ')[0].toLowerCase()}`}
                className={`px-3 py-1.5 text-sm border transition-colors ${products.includes(p) ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 hover:border-neutral-400'}`}>{p}</button>
            ))}
          </div>
        </div>
        <Field id="gq-details" label="What do you need? *">
          <Textarea id="gq-details" required rows={5} value={f.request_details} onChange={set('request_details')} placeholder="Rooms, window sizes if you know them, light control or privacy needs, motorization, timing…" data-testid="general-quote-details" />
        </Field>
        <Button type="submit" disabled={busy} className="btn-primary w-full sm:w-auto" data-testid="general-quote-submit">
          {busy ? 'Sending…' : 'Send quote request'} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </form>
    </div>
  );
};
